import { useEffect, useState } from 'react';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { AdminHeader } from "../components/AdminHeader";
import { AdminFooter } from "../components/AdminFooter";
import { signIn } from "../data/authService";
import { PasswordInput } from '@ui';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [isReturningUser] = useState(() => localStorage.getItem('gc_admin_visited') === 'true');

    useEffect(() => {
        if (!localStorage.getItem('gc_admin_visited')) {
            localStorage.setItem('gc_admin_visited', 'true');
        }
    }, []);

    const inputClasses =
        'w-full border border-[#2C2731] rounded-xl px-4 py-3 text-sm bg-[#FFFCEF] placeholder:text-[#2C2731]/50 focus:outline-none focus:ring-2 focus:ring-[#2C2731] focus:border-[#2C2731] transition-colors';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            await signIn(email.trim(), password);
            navigate('/admin', { replace: true });
        } catch (err: any) {
            setError(err?.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-[#FFFCEF] text-[#2C2731]">
            <AdminHeader showUserProfile={false} />

            <main className="flex-1 flex items-center justify-center px-4 py-10">
                <div className="max-w-md w-full text-center border border-[#2C2731] rounded-2xl bg-[#FFFCEF] px-8 py-10 shadow-sm">
                    <div className="h-14 w-14 mx-auto flex items-center justify-center rounded-full bg-[#E8DFBD] text-[#2C2731] mb-8">
                        <ArrowRightOnRectangleIcon className="h-6 w-6 text-[#2C2731]" />
                    </div>

                    <h1 className="text-3xl font-medium text-[#2C2731] mb-2 font-recoleta">
                        {isReturningUser ? 'Welcome Back' : 'Welcome'}
                    </h1>
                    <p className="text-[#2C2731]/80 mb-8">
                        Sign in to your admin account to manage guides, seekers, and reporting.
                    </p>

                    <form className="text-left space-y-4" onSubmit={handleSubmit}>
                        {error && (
                            <div className="p-4 bg-[#2C2731] border border-[#FEF6D6] rounded-xl text-[#FEF6D6]">
                                <div className="flex">
                                    <div className="flex-shrink-0 mt-0.5">
                                        <svg className="h-5 w-5 text-[#FEF6D6]" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm">
                                            {error === 'Invalid login credentials' || error.includes('Invalid') || error.includes('credentials') || error === 'Login failed'
                                                ? 'Incorrect email or password. Please check your details and try again.'
                                                : error}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-[#2C2731] mb-1">Email Address</label>
                            <input
                                type="email"
                                placeholder="Enter your email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={inputClasses}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-[#2C2731] mb-1">Password</label>
                            <PasswordInput
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={inputClasses}
                            />
                            <div className="text-right mt-2">
                                <button
                                    type="button"
                                    onClick={() => navigate('/auth/forgot')}
                                    className="text-sm text-[#2C2731]/70 hover:text-[#2C2731] transition-colors"
                                >
                                    Forgot password?
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#304031] text-sm font-semibold transition ${
                                loading
                                    ? 'bg-[#304031]/70 text-[#fef6d6] cursor-not-allowed'
                                    : 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
                            }`}
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin h-5 w-5 border-2 border-[#FEF6D6] border-t-transparent rounded-full"></div>
                                    Signing In...
                                </>
                            ) : (
                                <>
                                    <ArrowRightOnRectangleIcon className="h-5 w-5 text-[#fef6d6]" />
                                    Sign In
                                </>
                            )}
                        </button>
                    </form>

                    <p className="text-xs text-[#2C2731]/60 mt-6">
                        Secure login to access the admin console, guide management tools, and reporting.
                    </p>
                </div>
            </main>

            <AdminFooter />
        </div>
    );
}
